import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { RelabelCaseBuilderFormDataKey, CaseRow, RelabelProductRow } from "src/types";
import { isInvalidPurchaseOrderNumber } from "../stringValidation";

export class RelabelCaseBuilderFormValidator extends FormValidator {
  private static validatePurchaseOrderNumber(
    value: unknown
  ): ValidationError<RelabelCaseBuilderFormDataKey> | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    return isInvalidPurchaseOrderNumber(value)
      ? new ValidationError("Invalid purchase order number", "purchaseOrderId")
      : undefined;
  }

  private static validateCases(value: unknown): ValidationError<RelabelCaseBuilderFormDataKey> | undefined {
    const cases = value as CaseRow[];

    if (!cases || cases.length === 0) {
      return new ValidationError("At least one case is required", "cases");
    }

    for (const caseRow of cases) {
      const activeProducts = caseRow.products.filter((p) => Number(p.quantity) > 0);

      if (activeProducts.length === 0) {
        return new ValidationError(
          `Case ${caseRow.caseNumber} is empty — remove it or add products before submitting`,
          "cases"
        );
      }

      const routingTypes = new Set(activeProducts.map((p) => p.routingType));
      if (routingTypes.size > 1) {
        return new ValidationError(
          `Case ${caseRow.caseNumber}: Cannot generate — mixed stock types in a case`,
          "cases"
        );
      }

      if (activeProducts.length > 1) {
        return new ValidationError(
          `Case ${caseRow.caseNumber}: Cannot generate — each case may contain only one product`,
          "cases"
        );
      }
    }

    return undefined;
  }

  private static validateTransferOrderId(value: unknown): ValidationError<RelabelCaseBuilderFormDataKey> | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    return typeof value === "string" && /^TO-[A-Z0-9]+$/i.test(value)
      ? undefined
      : new ValidationError("Invalid transfer order id", "transferOrderId");
  }

  private static readonly validationStrategies: Record<
    RelabelCaseBuilderFormDataKey,
    (value: unknown) => ValidationError<RelabelCaseBuilderFormDataKey> | undefined
  > = {
    purchaseOrderId: RelabelCaseBuilderFormValidator.validatePurchaseOrderNumber,
    transferOrderId: RelabelCaseBuilderFormValidator.validateTransferOrderId,
    cases: RelabelCaseBuilderFormValidator.validateCases
  };

  static validateRelabelCaseBuilderFormData<T extends RelabelCaseBuilderFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(formData, RelabelCaseBuilderFormValidator.validationStrategies as never);
  }

  static validateProductDistribution(relabelProducts: RelabelProductRow[], cases: CaseRow[]): string | undefined {
    const inProgressByProductId = new Map<string, number>();
    for (const c of cases) {
      for (const p of c.products) {
        const q = Number(p.quantity) || 0;
        if (q > 0) inProgressByProductId.set(p.id, (inProgressByProductId.get(p.id) ?? 0) + q);
      }
    }

    for (const product of relabelProducts) {
      const inProgress = inProgressByProductId.get(product.id) ?? 0;
      if (inProgress === 0) continue;
      const available = Math.max(0, product.euReceivedQty - product.existingCasedQty);
      if (inProgress > available) {
        return `${product.skuName}: cannot distribute ${inProgress} units (only ${available} available)`;
      }
    }

    return undefined;
  }
}
